<script setup>
/**
 * The one dialog that opens a ticket, wherever the intent comes from.
 *
 * It decides nothing: it collects a type, a title, a priority, a first message
 * and — where the type carries customer communication responsibility — who
 * asked, and hands them up. The queue screen turns that into
 * `POST /support_cases`; the conversation Details panel turns it into
 * `POST /conversations/{id}/support_case_links`, which escalates the thread and
 * attaches it as the case's primary customer link. Neither shape is known here.
 *
 * The type list is the caller's — `GET /case_types`, which answers only the
 * types the permission scope grants — so this never offers a type whose cases
 * the agent could not then see. A caller with no types to offer gets a stated
 * empty state rather than an empty picker that reads as a broken control.
 *
 * The type also brings its own fields: `field_definitions` rides on that same
 * payload, so choosing a type is what puts them on screen and nothing extra is
 * fetched to do it. They are collected here rather than left for the workroom
 * because a required field is owed at create — the server refuses a new ticket
 * that has not answered one — and because an intake form that asks for less
 * than the type defines sends every agent to a second screen to finish the job.
 *
 * The requester is asked for only when the selected type's
 * `shareProgressWithCustomer` is on, which is the same switch the server
 * validates on — internal work has no customer to name, and a dev team's
 * tickets are not made to carry one. Escalating from a conversation, the thread
 * already names the customer, so it is stated rather than searched for.
 */
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import CaseFieldInput from './CaseFieldInput.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import { createContactSearcher } from 'dashboard/components-next/NewConversation/helpers/composeConversationHelper';
import {
  CASE_PRIORITY,
  CASE_PRIORITY_META,
} from 'dashboard/helper/supportCaseMeta';

const props = defineProps({
  // `{ id, name, shareProgressWithCustomer }` — the types this surface may open
  // a ticket under.
  caseTypes: {
    type: Array,
    default: () => [],
  },
  isSubmitting: {
    type: Boolean,
    default: false,
  },
  // A caller whose endpoint has no room for a first message says so, and the
  // field is not offered where it would be silently dropped.
  showDescription: {
    type: Boolean,
    default: true,
  },
  // Prefill, e.g. the conversation's subject.
  initialTitle: {
    type: String,
    default: '',
  },
  // `{ id, name }` — the conversation's contact, when the ticket is being
  // opened from a thread. Present means the requester is settled: it is shown,
  // not chosen.
  requesterContact: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['submit', 'close']);

const { t } = useI18n();

// The same searcher the compose flow uses: it aborts the request in flight when
// a newer one starts, so a fast typist never sees an older page of results.
const searchContacts = createContactSearcher();

// Most urgent first, the way the workroom's rail reads.
const PRIORITY_ORDER = [
  CASE_PRIORITY.URGENT,
  CASE_PRIORITY.HIGH,
  CASE_PRIORITY.MEDIUM,
  CASE_PRIORITY.LOW,
];

const dialogRef = ref(null);
const caseTypeId = ref(null);
const title = ref('');
const description = ref('');
const priority = ref('');
const requesterContactId = ref(null);
const contactResults = ref([]);
const customFields = ref({});

const typeOptions = computed(() =>
  props.caseTypes.map(caseType => ({
    value: caseType.id,
    label: caseType.name,
  }))
);

// "No priority" leads, because the column is nullable and leaving it unset is a
// real answer rather than a value the agent failed to pick.
const priorityOptions = computed(() => [
  { value: '', label: t('SUPPORT_CASES.CREATE.PRIORITY_NONE') },
  ...PRIORITY_ORDER.map(value => ({
    value,
    label: t(CASE_PRIORITY_META[value].labelKey),
  })),
]);

// The searcher answers only contacts with an email or a phone number, so every
// row has something to be named by even when the contact has no name.
const contactOptions = computed(() =>
  contactResults.value.map(contact => ({
    value: contact.id,
    label:
      contact.name && contact.email
        ? `${contact.name} (${contact.email})`
        : contact.name || contact.email || contact.phoneNumber,
  }))
);

const selectedType = computed(
  () =>
    props.caseTypes.find(caseType => caseType.id === caseTypeId.value) || null
);

// The type's own switch is the rule, here and on the server.
const needsRequester = computed(() =>
  Boolean(selectedType.value?.shareProgressWithCustomer)
);

const statedRequester = computed(() => props.requesterContact || null);

const requesterId = computed(
  () => statedRequester.value?.id || requesterContactId.value || null
);

// The selected type's own shapes, in the order its settings editor put them —
// the same order and the same list the workroom's Details panel renders.
const fieldDefinitions = computed(() =>
  [...(selectedType.value?.fieldDefinitions || [])].sort(
    (a, b) => a.position - b.position
  )
);

// `false` is a real answer to a boolean field and an empty multi-select is not
// an answer at all, so neither question can go through a plain falsy check.
const isAnswered = value => {
  if (value === false) return true;
  if (Array.isArray(value)) return value.length > 0;
  return value !== null && value !== undefined && value !== '';
};

// Every required field is owed on a new ticket — the server says so, and it
// says so by refusing the whole create. Asking here means the agent fixes it
// in the form they are already in rather than reading it back as an error.
const requiredFieldsAnswered = computed(() =>
  fieldDefinitions.value.every(
    definition =>
      !definition.required || isAnswered(customFields.value[definition.key])
  )
);

// Only what was actually answered: a key the agent left alone is omitted
// rather than sent as null, so the ticket stores no entry for it.
const answeredFields = computed(() =>
  Object.fromEntries(
    Object.entries(customFields.value).filter(([, value]) => isAnswered(value))
  )
);

// A single type is not a choice, so it is the answer.
const defaultTypeId = computed(() =>
  props.caseTypes.length === 1 ? props.caseTypes[0].id : null
);

const canSubmit = computed(
  () =>
    Boolean(caseTypeId.value && title.value.trim()) &&
    (!needsRequester.value || Boolean(requesterId.value)) &&
    requiredFieldsAnswered.value
);

// The keys belong to the type. Carrying answers across a type change would
// send keys the new type never defined, which it refuses outright.
watch(caseTypeId, () => {
  customFields.value = {};
});

const onContactSearch = async query => {
  const results = await searchContacts(query);
  // null means the request was aborted: a newer search is already in flight.
  if (results === null) return;
  contactResults.value = results;
};

const open = () => {
  caseTypeId.value = defaultTypeId.value;
  title.value = props.initialTitle;
  description.value = '';
  priority.value = '';
  requesterContactId.value = null;
  contactResults.value = [];
  customFields.value = {};
  dialogRef.value?.open();
};

const close = () => dialogRef.value?.close();

const onSubmit = () => {
  if (!canSubmit.value) return;
  emit('submit', {
    caseTypeId: caseTypeId.value,
    title: title.value.trim(),
    priority: priority.value || null,
    ...(props.showDescription
      ? { description: description.value.trim() || null }
      : {}),
    ...(needsRequester.value ? { requesterContactId: requesterId.value } : {}),
    ...(Object.keys(answeredFields.value).length
      ? { customFields: answeredFields.value }
      : {}),
  });
};

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('SUPPORT_CASES.CREATE.TITLE')"
    :description="t('SUPPORT_CASES.CREATE.SUBTITLE')"
    :confirm-button-label="t('SUPPORT_CASES.CREATE.SUBMIT')"
    :cancel-button-label="t('SUPPORT_CASES.CREATE.CANCEL')"
    :disable-confirm-button="!canSubmit"
    :is-loading="isSubmitting"
    :close-on-click-outside="false"
    @confirm="onSubmit"
    @close="emit('close')"
  >
    <div class="flex flex-col gap-4" data-testid="case-create-dialog">
      <p
        v-if="!caseTypes.length"
        class="mb-0 text-sm text-n-slate-11"
        data-testid="case-create-no-types"
      >
        {{ t('SUPPORT_CASES.CREATE.NO_TYPES') }}
      </p>

      <div v-else class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('SUPPORT_CASES.CREATE.TYPE_LABEL') }}
        </label>
        <ComboBox
          v-model="caseTypeId"
          :options="typeOptions"
          :placeholder="t('SUPPORT_CASES.CREATE.TYPE_PLACEHOLDER')"
          data-testid="case-create-type"
        />
      </div>

      <Input
        v-model="title"
        :label="t('SUPPORT_CASES.CREATE.TITLE_LABEL')"
        :placeholder="t('SUPPORT_CASES.CREATE.TITLE_PLACEHOLDER')"
        data-testid="case-create-title"
      />

      <div v-if="caseTypes.length" class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('SUPPORT_CASES.CREATE.PRIORITY_LABEL') }}
        </label>
        <Select
          v-model="priority"
          :options="priorityOptions"
          data-testid="case-create-priority"
        />
      </div>

      <div
        v-if="needsRequester"
        class="flex flex-col gap-1"
        data-testid="case-create-requester"
      >
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('SUPPORT_CASES.CREATE.REQUESTER_LABEL') }}
        </label>

        <span
          v-if="statedRequester"
          class="px-3 py-2 text-sm truncate rounded-lg bg-n-alpha-1 text-n-slate-12"
          data-testid="case-create-requester-name"
        >
          {{ statedRequester.name }}
        </span>

        <ComboBox
          v-else
          v-model="requesterContactId"
          use-api-results
          :options="contactOptions"
          :placeholder="t('SUPPORT_CASES.CREATE.REQUESTER_PLACEHOLDER')"
          :search-placeholder="t('SUPPORT_CASES.CREATE.REQUESTER_SEARCH')"
          :empty-state="t('SUPPORT_CASES.CREATE.REQUESTER_EMPTY')"
          data-testid="case-create-requester-picker"
          @search="onContactSearch"
        />

        <p class="mb-0 text-xs text-n-slate-11">
          {{ t('SUPPORT_CASES.CREATE.REQUESTER_HINT') }}
        </p>
      </div>

      <div
        v-for="definition in fieldDefinitions"
        :key="definition.id"
        class="flex flex-col gap-1"
        :data-testid="`case-create-field-${definition.key}`"
      >
        <label
          :for="`case-field-input-${definition.id}`"
          class="text-sm font-medium text-n-slate-12"
        >
          {{ definition.label }}
          <span v-if="definition.required" class="text-n-ruby-11">*</span>
        </label>
        <CaseFieldInput
          :definition="definition"
          :model-value="customFields[definition.key] ?? null"
          size="md"
          @update:model-value="customFields[definition.key] = $event"
        />
      </div>

      <TextArea
        v-if="showDescription"
        v-model="description"
        :label="t('SUPPORT_CASES.CREATE.DESCRIPTION_LABEL')"
        :placeholder="t('SUPPORT_CASES.CREATE.DESCRIPTION_PLACEHOLDER')"
        :max-length="2000"
        data-testid="case-create-description"
      />
    </div>
  </Dialog>
</template>
